import React, { useEffect, useState } from "react";
import { useApi } from "@/";
import { useSelector } from "react-redux";
import { MdOutlineKeyboardArrowLeft, MdOutlineKeyboardArrowRight, MdOutlineKeyboardDoubleArrowRight, MdOutlineKeyboardDoubleArrowLeft } from "react-icons/md";

const PincodeTable = () => {
  const { callApi } = useApi();
  const couriers = useSelector((state) => state.app.appData.courier) || [];
  const [data, setData] = useState([]);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    pages: 0,
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPincodes = async () => {
      setLoading(true);
      try {
        const response = await callApi("/admin/getPincodes", "GET", null, {
          page: pagination.page,
          limit: pagination.limit,
        });
        
        if (!response.success) {
          console.log("Failed to fetch pincodes:-", response?.error?.message || response?.error);
          return;
        }
        
        setData(response.data.pincodes);
        setPagination({
          page: parseInt(response.data.pagination.page) || 1,
          limit: parseInt(response.data.pagination.limit) || 10,
          total: parseInt(response.data.pagination.total) || 0,
          pages: parseInt(response.data.pagination.pages) || 0,
        });
      } catch (error) {
        console.error("Error fetching pincodes:", error.message || error);
      } finally {
        setLoading(false);
      }
    };

    fetchPincodes();
  }, [pagination.page, pagination.limit, callApi]);

  const handlePageChange = (newPage) => {
    if (newPage > 0 && newPage <= pagination.pages) {
      setPagination((prev) => ({ ...prev, page: newPage }));
    }
  };

  const handleLimitChange = (event) => {
    const newLimit = parseInt(event.target.value);
    setPagination((prev) => ({ ...prev, page: 1, limit: newLimit }));
  };

  if (loading) return <div className="p-4 text-center">Loading pincodes...</div>;

  return (
    <div className="bg-background_2 !rounded-xl shadow-lg overflow-x-auto border border-secondary/20">
      <table className="w-full bg-background_2 font-nunito rounded-xl border border-secondary/20 min-w-max">
        <thead>
          <tr className="bg-background_2 ">
            <th className="border border-secondary/20 p-1.5 text-secondary/90 font-semibold max-w-[150px]">Pincode</th>
            <th className="border border-secondary/20 p-1.5 text-secondary/90 font-semibold max-w-[150px]">City</th>
            <th className="border border-secondary/20 p-1.5 text-secondary/90 font-semibold max-w-[150px]">State</th>
            <th className="border border-secondary/20 p-1.5 text-secondary/90 font-semibold max-w-[150px]">State Cluster</th>
            <th className="border border-secondary/20 p-1.5 text-secondary/90 font-semibold max-w-[150px]">Metro</th>
            <th className="border border-secondary/20 p-1.5 text-secondary/90 font-semibold max-w-[150px]">Service Level</th>
            {couriers?.map((courier) => (
              <th key={courier?._id} className="border border-secondary/30 text-secondary/90 font-semibold max-w-[200px]">
                {courier?.name}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {data?.length > 0 ? (
            data.map((item) => (
              <tr key={item._id} className="odd:bg-background/50 even:bg-background">
                <td className="border border-secondary/20 p-1 text-center">{item.pincode}</td>
                <td className="border border-secondary/20 p-1 text-center">{item.city}</td>
                <td className="border border-secondary/20 p-1 text-center">{item.state}</td>
                <td className="border border-secondary/20 p-1 text-center">{item.stateCluster}</td>
                <td className="border border-secondary/20 p-1 text-center">{item.metro ? "Yes" : "No"}</td>
                <td className="border border-secondary/20 p-1 text-center">{item.serviceLevel ? "High" : "Low"}</td>
                {couriers?.map((courier) => {
                  const partner = item.courierPartners.find((p) => p.courierId?._id === courier?._id);
                  return (
                    <td key={courier?._id} className="border border-secondary/20 p-1 text-center max-w-[200px]">
                      {partner ? (
                        <table className="w-full text-xs text-start text-secondary/90">
                          <tbody>
                            <tr><td className="font-bold">Air</td><td>{partner.airService ? "Yes" : "No"}</td></tr>
                            <tr><td className="font-bold">Surface</td><td>{partner.surfaceService ? "Yes" : "No"}</td></tr>
                            <tr><td className="font-bold">Pickup</td><td>{partner.pickup ? "Yes" : "No"}</td></tr>
                            <tr><td className="font-bold">Delivery</td><td>{partner.delivery ? "Yes" : "No"}</td></tr>
                            <tr><td className="font-bold">COD</td><td>{partner.codAvailable ? "Yes" : "No"}</td></tr>
                          </tbody>
                        </table>
                      ) : (
                        "N/A"
                      )}
                    </td>
                  );
                })}
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan={6 + couriers?.length} className="text-center p-4">
                No pincodes found
              </td>
            </tr>
          )}
        </tbody>
      </table>
      
      <div className="flex flex-col sm:flex-row justify-between items-center px-4 py-3 gap-2">
        <div className="flex items-center gap-2">
          <span className="text-sm">Show</span>
          <select 
            value={pagination.limit} 
            onChange={handleLimitChange}
            className="border border-secondary/30 bg-secondary/90 hover:bg-secondary/80 text-background/90 rounded-lg p-1 pl-2"
          >
            <option value="10" className="text-background/90">10</option>
            <option value="25" className="text-background/90">25</option>
            <option value="50" className="text-background/90">50</option>
            <option value="100" className="text-background/90">100</option>
          </select>
          <span className="text-sm">entries</span>
        </div>
        
        <div className="flex items-center gap-2">
          <button 
            disabled={pagination.page === 1} 
            onClick={() => handlePageChange(1)}
            className="px-2 py-1.5 bg-secondary/90 text-sm text-background/90 hover:bg-secondary/70 rounded-lg disabled:opacity-50"
          >
            <MdOutlineKeyboardDoubleArrowLeft size={20}/>
          </button>
          <button 
            disabled={pagination.page === 1} 
            onClick={() => handlePageChange(pagination.page - 1)}
            className="px-2 py-1.5 bg-secondary/90 text-sm text-background/90 hover:bg-secondary/70 rounded-lg disabled:opacity-50"
          >
            <MdOutlineKeyboardArrowLeft size={20}/>
          </button>
          
          <span className="mx-2 text-sm">
            Page <b>{pagination.page}</b> of <b>{pagination.pages || 1}</b>
          </span>
          
          <button 
            disabled={pagination.page === pagination.pages || pagination.pages === 0} 
            onClick={() => handlePageChange(pagination.page + 1)}
            className="px-2 py-1.5 bg-secondary/90 text-sm text-background/90 hover:bg-secondary/70 rounded-lg disabled:opacity-50"
          >
          <MdOutlineKeyboardArrowRight size={20}/>
          </button>
          <button 
            disabled={pagination.page === pagination.pages || pagination.pages === 0} 
            onClick={() => handlePageChange(pagination.pages)}
            className="px-2 py-1.5 bg-secondary/90 text-sm text-background/90 hover:bg-secondary/70 rounded-lg disabled:opacity-50"
          >
            <MdOutlineKeyboardDoubleArrowRight size={20}/>
          </button>
        </div>
      </div>
    </div>
  );
};

export default PincodeTable;
